import { useState } from "react";
import { archiveAndResetAppData, recoveryCopies, type StorageIssue } from "./platform/storage";

export function downloadOriginal(raw: string, name = "haebolcae-original.txt") {
  const url = URL.createObjectURL(new Blob([raw], { type: "text/plain;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export function StorageRecovery({ issue, retry }: { issue: StorageIssue; retry: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    if (issue.raw === undefined) return;
    setBusy(true);
    try { await archiveAndResetAppData(issue.raw); retry(); }
    catch { setError("원본 보관이나 새 준비표 저장을 확인하지 못했어요. 원본을 내려받고 저장 공간을 확인해 주세요."); }
    finally { setBusy(false); }
  };
  return <section className="storage-recovery" aria-labelledby="recovery-title">
    <h1 id="recovery-title">저장된 준비표를 먼저 확인해 주세요</h1>
    <p>{issue.kind === "unsupported_version" ? "다른 버전에서 저장한 기록이라 지금은 열 수 없어요." : issue.kind === "unavailable" ? "기기 저장 공간에 접근할 수 없어요. 브라우저의 저장 허용 상태를 확인해 주세요." : "저장된 기록을 읽지 못했어요."} 자동 저장을 멈췄어요.</p>
    <p>앱을 다시 열거나 기록을 만든 버전에서 확인할 수 있어요. 원본은 해볼캐가 수정하지 않았어요.</p>
    <div className="data-button-row">
      <button type="button" className="button primary" onClick={retry}>다시 확인</button>
      {issue.raw !== undefined && <button type="button" className="button" onClick={() => issue.raw !== undefined && downloadOriginal(issue.raw)}>원본 내려받기</button>}
    </div>
    {issue.raw !== undefined && <details className="recovery-choice"><summary>파일이 받아지지 않으면 원본 텍스트 보기</summary><p>아래 내용을 모두 선택해 내 기기의 메모나 파일에 보관할 수 있어요.</p><textarea aria-label="저장된 원본 텍스트" readOnly value={issue.raw} rows={6} /></details>}
    {issue.raw !== undefined && <div className="recovery-choice">
      <h2>빈 준비표로 새로 시작하려면</h2>
      <p>원본을 이 기기에 별도로 보관한 뒤 새로 시작해요. 보관본은 ‘내 데이터’에서 다시 내려받을 수 있어요. 기기 저장 공간을 지우면 보관본도 사라지니 먼저 파일로 보관해 주세요.</p>
      <label><input type="checkbox" checked={confirm} onChange={(event) => setConfirm(event.target.checked)} /> 기존 기록 대신 빈 준비표를 여는 것을 확인했어요</label>
      <button type="button" className="button" disabled={!confirm || busy} onClick={() => void reset()}>{busy ? "원본 보관 확인 중…" : "원본 보관 후 새로 시작"}</button>
    </div>}
    {error && <p role="alert">{error}</p>}
  </section>;
}

export function RecoveryCopies() {
  const [copies] = useState(() => {
    try { return recoveryCopies(); } catch { return null; }
  });
  if (copies?.length === 0) return null;
  return <section className="recovery-choice" aria-labelledby="recovery-copies-title">
    <h2 id="recovery-copies-title">따로 보관한 원본</h2>
    {copies === null ? <p>기기 보관본을 읽지 못했어요. 저장 허용 상태를 확인하고 다시 열어 주세요.</p> : <>
      <p>손상되거나 다른 버전인 원본을 그대로 보관했어요. 준비표로 복구된 상태는 아니에요.</p>
      <ul>{copies.map((copy, index) => <li key={copy.key}><button type="button" className="button" onClick={() => downloadOriginal(copy.raw, `haebolcae-original-${index + 1}.txt`)}>보관 원본 {index + 1} 내려받기</button><details><summary>보관 원본 {index + 1} 텍스트 보기</summary><textarea aria-label={`보관 원본 ${index + 1} 텍스트`} readOnly value={copy.raw} rows={6} /></details></li>)}</ul>
    </>}
  </section>;
}
